// pages/EditContent.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Paper,
  Alert,
  CircularProgress,
  Chip,
  Stack,
  Grid,
  InputAdornment,
  Divider,
} from '@mui/material';
import { 
  Save, 
  Lightbulb, 
  School, 
  RateReview, 
  Science, 
  Psychology,
  EditNote,
  Description,
  AutoAwesome,
} from '@mui/icons-material';
import { db } from '../../lib/firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import AdvancedRichTextEditor from '../../components/AdvancedRichTextEditor';

const EditContent = () => {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    category: 'Draft',
    tags: [],
  });
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [titleError, setTitleError] = useState('');
  const [contentError, setContentError] = useState('');
  const [originalData, setOriginalData] = useState(null);
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  // Categories for content library with icons
  const categories = [
    { value: 'Draft', label: 'Draft', icon: <EditNote />, color: '#9E9E9E', description: 'Work in progress' },
    { value: 'Idea', label: 'Idea', icon: <Lightbulb />, color: '#FFC107', description: 'Brainstormed concepts' },
    { value: 'Research', label: 'Research', icon: <Science />, color: '#2196F3', description: 'Research materials' },
    { value: 'Tutorial', label: 'Tutorial', icon: <School />, color: '#4CAF50', description: 'Step-by-step guides' },
    { value: 'Review', label: 'Review', icon: <RateReview />, color: '#FF9800', description: 'Product/service reviews' },
    { value: 'Opinion', label: 'Opinion', icon: <Psychology />, color: '#9C27B0', description: 'Personal perspectives' },
    { value: 'Interview', label: 'Interview', icon: <Description />, color: '#00BCD4', description: 'Q&A sessions' },
    { value: 'Case Study', label: 'Case Study', icon: <AutoAwesome />, color: '#E91E63', description: 'In-depth analysis' },
  ];

  useEffect(() => {
    fetchContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchContent = async () => {
    try {
      const contentDoc = await getDoc(doc(db, 'contents', id));
      if (contentDoc.exists()) {
        const data = contentDoc.data();
        if (currentUser?.uid !== data.userId) {
          navigate('/admin/content');
          return;
        }
        const formDataObj = {
          title: data.title || '',
          content: data.content || '',
          excerpt: data.excerpt || '',
          category: data.category || 'Draft',
          tags: data.tags || [],
        };
        setFormData(formDataObj);
        setOriginalData(formDataObj);
      } else {
        navigate('/admin/content');
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      setError('Failed to load content');
    } finally {
      setFetching(false);
    }
  };

  const handleAddTag = () => {
    const trimmedTag = tagInput.trim();
    
    if (!trimmedTag) return;
    
    // Split tags by comma
    const newTags = trimmedTag.split(',').map(tag => tag.trim()).filter(tag => tag);
    
    // Filter out empty tags and duplicates
    const uniqueTags = [...new Set([...formData.tags, ...newTags])];
    
    // Remove any emojis or special characters (allow letters, numbers, spaces, hyphens)
    const cleanTags = uniqueTags.map(tag => 
      tag.replace(/[^\w\s-]/g, '').replace(/\s+/g, ' ').trim()
    ).filter(tag => tag.length > 0 && tag.length <= 30);
    
    if (cleanTags.length > 0) {
      setFormData({
        ...formData,
        tags: cleanTags,
      });
      setTagInput('');
      setError('');
    }
  };

  const handleTagInputKeyPress = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(tag => tag !== tagToRemove),
    });
  };

  const validateForm = () => {
    let isValid = true;
    
    if (!formData.title.trim()) {
      setTitleError('Please enter a title');
      isValid = false;
    } else if (formData.title.length < 3) {
      setTitleError('Title must be at least 3 characters');
      isValid = false;
    } else if (formData.title.length > 150) {
      setTitleError('Title must be less than 150 characters');
      isValid = false;
    } else {
      setTitleError('');
    }
    
    if (!formData.content.trim()) {
      setContentError('Please enter content');
      isValid = false;
    } else if (formData.content.length < 20) {
      setContentError('Content must be at least 20 characters');
      isValid = false;
    } else {
      setContentError('');
    }
    
    return isValid;
  };

  const hasChanges = () => {
    if (!originalData) return false;
    return (
      formData.title !== originalData.title ||
      formData.content !== originalData.content ||
      formData.excerpt !== originalData.excerpt ||
      formData.category !== originalData.category ||
      JSON.stringify(formData.tags) !== JSON.stringify(originalData.tags)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!hasChanges()) {
      setError('No changes to save');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Generate excerpt from content if not provided
      let excerpt = formData.excerpt.trim();
      if (!excerpt) {
        // Strip HTML tags and get plain text
        const plainText = formData.content.replace(/<[^>]*>/g, '');
        excerpt = plainText.substring(0, 200) + (plainText.length > 200 ? '...' : '');
      }

      const contentData = {
        title: formData.title.trim(),
        content: formData.content,
        excerpt: excerpt,
        category: formData.category,
        tags: formData.tags,
        updatedAt: serverTimestamp(),
        wordCount: formData.content.replace(/<[^>]*>/g, '').split(/\s+/).length,
        characterCount: formData.content.replace(/<[^>]*>/g, '').length,
      };

      await updateDoc(doc(db, 'contents', id), contentData);
      
      setSuccess('Content updated successfully! Redirecting...');
      setTimeout(() => {
        navigate('/admin/content');
      }, 1500);
    } catch (err) {
      console.error('Error updating content:', err);
      setError('Failed to update content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getWordCount = () => {
    const plainText = formData.content.replace(/<[^>]*>/g, '');
    const words = plainText.trim().split(/\s+/).filter(word => word.length > 0);
    return words.length;
  };

  if (fetching) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
        <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" sx={{ mb: 1 }}>
          Edit Content
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Update your content. All changes will be saved to the content library.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {/* Title Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Content Title"
              value={formData.title}
              onChange={(e) => {
                setFormData({ ...formData, title: e.target.value });
                if (titleError) setTitleError('');
              }}
              required
              variant="outlined"
              placeholder="Enter a descriptive title for your content..."
              error={!!titleError}
              helperText={titleError || `${formData.title.length}/150 characters`}
              inputProps={{ maxLength: 150 }}
            />
          </Box>

          {/* Category Selection with Icons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Content Category
            </Typography>
            <Grid container spacing={1}>
              {categories.map((cat) => (
                <Grid item key={cat.value}>
                  <Button
                    variant={formData.category === cat.value ? 'contained' : 'outlined'}
                    onClick={() => setFormData({ ...formData, category: cat.value })}
                    startIcon={cat.icon}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 3,
                      borderColor: formData.category === cat.value ? cat.color : undefined,
                      backgroundColor: formData.category === cat.value ? cat.color : undefined,
                      '&:hover': {
                        backgroundColor: formData.category === cat.value ? cat.color : undefined,
                        opacity: 0.9,
                      },
                    }}
                  >
                    {cat.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
              {categories.find(cat => cat.value === formData.category)?.description}
            </Typography>
          </Box>

          {/* Excerpt Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Excerpt (Optional)"
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              multiline
              rows={2}
              variant="outlined"
              placeholder="A short summary of your content (will be auto-generated from content if left empty)..."
              helperText={`${formData.excerpt.length}/300 characters`}
              inputProps={{ maxLength: 300 }}
            />
          </Box>

          {/* Tags Input with Comma Separation */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Tags
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Add tags separated by commas (e.g., React, JavaScript, Web Development)..."
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleTagInputKeyPress}
              onBlur={handleAddTag}
              helperText="Press Enter, comma, or click Add Tag to add multiple tags"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Button 
                      variant="text" 
                      onClick={handleAddTag}
                      size="small"
                    >
                      Add Tag
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            {formData.tags.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {formData.tags.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleRemoveTag(tag)}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
                </Stack>
              </Box>
            )}
          </Box>

          {/* Content Editor */}
          <Box sx={{ mb: 3 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                Content
              </Typography>
              <Box display="flex" gap={2}>
                <Typography variant="caption" color="textSecondary">
                  Words: {getWordCount()}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Characters: {formData.content.replace(/<[^>]*>/g, '').length}
                </Typography>
              </Box>
            </Box>
            <AdvancedRichTextEditor
              value={formData.content}
              onChange={(content) => {
                setFormData({ ...formData, content });
                if (contentError) setContentError('');
              }}
              placeholder="Edit your content here. You can use formatting tools, add images, tables, code blocks, and more..."
            />
            {contentError && (
              <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block' }}>
                {contentError}
              </Typography>
            )}
          </Box>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box display="flex" gap={2} justifyContent="flex-end">
            <Button 
              variant="outlined" 
              onClick={() => {
                if (hasChanges()) {
                  if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
                    navigate('/admin/content');
                  }
                } else {
                  navigate('/admin/content');
                }
              }}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={20} /> : <Save />}
              disabled={loading || !hasChanges()}
              sx={{ borderRadius: 2 }}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>

          {/* Save Status Indicator */}
          {hasChanges() && (
            <Typography variant="caption" color="warning.main" sx={{ mt: 2, display: 'block', textAlign: 'right' }}>
              You have unsaved changes
            </Typography>
          )}
        </form>
      </Paper>
    </Container>
  );
};

export default EditContent;